import { RISKS_TYPES } from 'utils/enums';

export const resumePolicyPathNames = {
	resumePolicyDraft: 'resume-pd', // pd - policy draft
	resumeUWCReferredPolicy: 'resume-uwcrp', // uwcrp - under writer continuing referred policy
	resumeUCReferredPolicy: 'resume-ucrp', // ucrp - user continuing referred policy
	resumeUWCEndorsement: 'resume-uwcedr', // uwcedr - under writer continuing endorsement
	resumeUCQuotePolicy: 'resume-ucqp', // ucqp - user continuing quote policy
	resumeUWCLeadPolicy: 'resume-uwcl' // uwclp - under writer continuing lead policy
};

/**
 * Builds the full SavePolicyEdits payload: risk attribute diffs and (for endorsements) a price comparison.
 * Mirrors the comparison logic in Ov_PolicySummary.js and the display logic in EndorsePriceComparison.js.
 *
 * @param {Array} risks - Redux fuse.form.risks (sparse array of { 'risks-N-M': riskObject })
 * @param {Array} originalRisks - draftPolicy.resumePolicyResponse.currentVersionRisks (flat array with code + attributes)
 * @param {object} formValues - Redux fuse.form.formValues (attribute values live here as `${fieldKey}-${att.name}`)
 * @param {object} [options]
 * @param {boolean} [options.isEndorsement] - When true, priceComparison is included; omitted for amendments.
 * @param {object} [options.existingPolicyPremiumSummary] - Pre-endorsement premium snapshot from productPriceNew.value
 * @param {object} [options.productRating] - Selected product option rating at the chosen frequency
 * @returns {{ risks: Array, priceComparison: object|undefined }}
 */
export const buildPolicyChangesJson = (
	risks,
	originalRisks,
	formValues,
	{ isEndorsement, existingPolicyPremiumSummary, productRating } = {}
) => {
	if (!originalRisks?.length) return { risks: [], priceComparison: undefined };

	// Flatten while preserving the field key (e.g. 'risks-0-0') so we can read current values
	// from formValues using the same `${fieldKey}-${att.name}` pattern as getRisks() in CreatePolicyHelper.
	const flatCurrentRisks = risks.flatMap(page =>
		page ? Object.entries(page).map(([field, risk]) => ({ field, risk })) : []
	);

	const riskDiffs = flatCurrentRisks
		.filter(({ risk }) => risk.riskType === RISKS_TYPES.INFORMATION_RISK)
		.map(({ field, risk }) => {
			const originalRisk = originalRisks.find(or => or.code === risk.code);
			// Exclude display-only code attributes (dataTypes 26 = mobile code, 12 = currency code).
			const attributes = risk.attributes
				.filter(att => ![26, 12].includes(att.dataType))
				.map(att => {
					const originalValue = originalRisk?.attributes?.find(oa => oa.code === att.code)?.value ?? null;
					// Current values are stored in formValues under `${fieldKey}-${att.name}`, mirroring getRisks().
					const value = formValues[`${field}-${att.name}`] ?? null;
					return {
						code: att.code,
						name: att.name,
						originalValue,
						value,
						hasChanged: originalValue !== value,
						dataType: att.dataType ?? null
					};
				});
			return { id: risk.id ?? null, code: risk.code, name: risk.name, attributes };
		});

	// priceComparison mirrors EndorsePriceComparison.js exactly; undefined for amendments so JSON.stringify omits it.
	const priceComparison = isEndorsement
		? {
				current: {
					recurringPremiumAmount: existingPolicyPremiumSummary?.recurringPremiumAmount ?? null,
					totalAmount: existingPolicyPremiumSummary?.totalAmount ?? null,
					nextCollectionDate: existingPolicyPremiumSummary?.nextCollectionDate ?? null
				},
				afterEndorsement: {
					recurringPremiumAmount: productRating?.ratingOutfit?.recurringPremiumAmount ?? null,
					totalAmount: productRating?.ratingOutfit?.annualPremium ?? null,
					nextCollectionAmount: productRating?.ratingOutfit?.policyCollectionAmount ?? null,
					// Prefer updated date from rating; fall back to existing policy date (mirrors sourceTag logic).
					nextCollectionDate:
						productRating?.ratingOutfit?.nextCollectionDate ??
						existingPolicyPremiumSummary?.nextCollectionDate ??
						null
				}
			}
		: undefined;

	return { risks: riskDiffs, priceComparison };
};

/**
 * Builds the checkout request payload for an endorsement.
 *
 * @param {object} params
 * @param {string} params.policyReferenceId - Policy reference ID (draftPolicy.pathParam2)
 * @param {string} params.policySaleReference - Policy sale reference (draftPolicy.pathParam3)
 * @param {object} params.agentDetails - Agent details from referenceTokenDetails
 * @param {object} params.policyHolderDetails - Policyholder fields from getPolicyHolderDetails
 * @param {boolean} params.isEndorsable - Whether the policy is endorsable
 * @param {object} params.ratingOutfit - Rating outfit from the selected product option/frequency
 * @param {object} params.channelSettings - Channel settings from context
 * @param {string} params.paymentInstanceId - Payment instance ID from resume policy response (draftPolicy.resumePolicyResponse.collsPaymentInstanceId)
 * @param {object} params.existingPolicyPremiumSummary - Pre-endorsement premium breakdown from productPriceNew.value
 */
export const buildEndorseCheckoutBody = ({
	policyReferenceId,
	policySaleReference,
	agentDetails,
	policyHolderDetails,
	isEndorsable,
	ratingOutfit,
	channelSettings,
	paymentInstanceId,
	existingPolicyPremiumSummary
}) => {
	// Spread policyholder fields so the helper owns all field references
	const { phPhoneNumberCode, phPhoneNumber, phEmail, phFirstName, phLastName } = policyHolderDetails;
	const {
		immediateProRataPremium,
		policyCollectionAmount,
		nextCollectionAmount,
		nextCollectionDate,
		nextCollectionDay,
		recurringPremiumAmount,
		recurringPremiumsCount
	} = ratingOutfit || {};

	return {
		paymentInstanceId: paymentInstanceId || null,
		companyId: agentDetails?.companyInstanceId,
		companyCode: agentDetails?.partnerCode,
		policyReferenceId,
		policySaleReference,
		amount: 0,
		currency: channelSettings?.currencyCode || 'ZAR',
		checkoutType: 0,
		shopperSuccessUrl: ``,
		shopperFailureUrl: ``,
		submitXPayment: false,
		makePolicySalePayment: false,
		defaultPercentage: 100,
		agentName: `${agentDetails?.agentFirstName} ${agentDetails?.agentLastName}`,
		agentEmail: agentDetails?.agentEMail,
		mobile: `+${phPhoneNumberCode}${phPhoneNumber}`,
		email: phEmail,
		debtorName: `${phFirstName} ${phLastName}`,
		isRetry: false,
		endorsementRequest: {
			isEndorsement: isEndorsable,
			immediateProRataPremium: immediateProRataPremium != null ? parseFloat(immediateProRataPremium) : null,
			nextCollectionAmount: nextCollectionAmount != null ? parseFloat(nextCollectionAmount) : null,
			nextCollectionDate: nextCollectionDate ?? existingPolicyPremiumSummary?.nextCollectionDate,
			nextCollectionDay: (() => {
				const day = nextCollectionDay ?? existingPolicyPremiumSummary?.nextCollectionDay;
				return day != null ? parseInt(day, 10) : null;
			})(),
			recurringPremiumAmount: recurringPremiumAmount != null ? parseFloat(recurringPremiumAmount) : null,
			recurringPremiumsCount: recurringPremiumsCount != null ? parseInt(recurringPremiumsCount, 10) : null,
			totalAmount: policyCollectionAmount != null ? parseFloat(policyCollectionAmount) : null
		}
	};
};
